import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { EnvironmentalPermit, EnvPermitState, EnvPermitType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import {
  Detail,
  EmptyState,
  ErrorNote,
  Field,
  FileHashField,
  Modal,
  PageHeader,
  RoleNotice,
  Spinner,
  StatusBadge,
  type FileHashValue,
} from '../../ui/components';

const OFFICER_ROLES = ['ENV_OFFICER'];
const PERMIT_TYPES: EnvPermitType[] = ['TREE_REMOVAL', 'RECLAMATION', 'DISCHARGE', 'DREDGING', 'SAND_EXTRACTION'];
const STATES: EnvPermitState[] = ['APPLIED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'ISSUED', 'COMPLETED', 'REVOKED'];

/** Environmental permits: tree removal / reclamation / discharge / dredging / sand extraction (M31). */
export function Permits() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENV_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showApply, setShowApply] = useState(false);
  const [showQuickTree, setShowQuickTree] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m31/permits?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<EnvironmentalPermit>>(path);

  return (
    <div>
      <PageHeader
        title="Environmental Permits"
        subtitle="Apply -> review -> decide -> issue (DocVault) -> complete/revoke. Tree removal also has a quick mobile photo+GPS path."
        actions={
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setShowQuickTree(true)}>
              + Quick tree removal (mobile)
            </button>
            <button className="btn-primary" onClick={() => setShowApply(true)}>
              + New permit application
            </button>
          </div>
        }
      />

      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAppliedQ(q);
        }}
      >
        <input className="field-input !w-64" placeholder="Ref or description…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary">
          Search
        </button>
      </form>

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Type</th>
                <th className="table-head">Parcel</th>
                <th className="table-head">Fee</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.refNo}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.type} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{p.parcelNo}</td>
                  <td className="table-cell text-xs">
                    {scr(p.feeAmountScr)} · <StatusBadge status={p.feeStatus} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={p.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <PermitDetailModal
          id={selectedId}
          isOfficer={isOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showApply ? (
        <ApplyModal
          onClose={() => setShowApply(false)}
          onDone={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}
      {showQuickTree ? (
        <QuickTreeModal
          onClose={() => setShowQuickTree(false)}
          onDone={() => {
            setShowQuickTree(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PermitDetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<EnvironmentalPermit>(`/api/v1/m31/permits/${id}`);
  const p = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approve, setApprove] = useState<boolean | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={p ? p.refNo : 'Environmental permit'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <Detail label="Type" value={<StatusBadge status={p.type} />} />
            <Detail label="State" value={<StatusBadge status={p.state} />} />
            <Detail label="Fee" value={`${scr(p.feeAmountScr)} (${p.feeStatus})`} />
            <Detail label="Parcel" value={p.parcelNo} mono />
            <Detail label="Applicant" value={`${p.applicantId} (${p.applicantType})`} mono />
            {p.eiaCaseId ? <Detail label="Linked EIA case" value={p.eiaCaseId} mono /> : null}
            {p.permitSerial ? <Detail label="Permit serial" value={p.permitSerial} mono /> : null}
            {p.gpsLat !== null && p.gpsLng !== null ? <Detail label="GPS" value={`${p.gpsLat.toFixed(5)}, ${p.gpsLng.toFixed(5)}`} mono /> : null}
            {p.validTo ? <Detail label="Valid to" value={formatDate(p.validTo)} /> : null}
          </div>
          <p className="text-sm text-slate-700">{p.description}</p>
          {p.details ? <p className="text-xs text-slate-500">Details: {Object.entries(p.details).map(([k, v]) => `${k}=${v}`).join(', ')}</p> : null}
          {p.rejectionReason ? <ErrorNote message={`Rejected: ${p.rejectionReason}`} /> : null}
          {p.revokedReason ? <ErrorNote message={`Revoked: ${p.revokedReason}`} /> : null}

          {p.feeStatus !== 'PAID' ? <p className="text-xs text-amber-600">Fee not yet confirmed paid (SeyPay webhook, service-token only).</p> : null}

          {isOfficer && p.state === 'APPLIED' ? (
            <button className="btn-primary" disabled={busy || p.feeStatus !== 'PAID'} onClick={() => act(() => api.post(`/api/v1/m31/permits/${id}/start-review`))}>
              Start review
            </button>
          ) : null}

          {isOfficer && p.state === 'UNDER_REVIEW' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Decision</div>
              <div className="flex gap-2">
                <button className={`btn-success ${approve === true ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(true)}>
                  Approve
                </button>
                <button className={`btn-danger ${approve === false ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(false)}>
                  Reject
                </button>
              </div>
              {approve !== null ? (
                <>
                  <input className="field-input" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  {approve === false ? (
                    <input className="field-input" placeholder="Rejection reason (required)" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
                  ) : null}
                  <button
                    className={approve ? 'btn-success' : 'btn-danger'}
                    disabled={busy || (approve === false && rejectionReason.trim().length === 0)}
                    onClick={() => act(() => api.post(`/api/v1/m31/permits/${id}/decide`, { approve, notes: notes || undefined, rejectionReason: approve ? undefined : rejectionReason }))}
                  >
                    Confirm {approve ? 'approval' : 'rejection'}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}

          {isOfficer && p.state === 'APPROVED' ? (
            <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/permits/${id}/issue`))}>
              Issue permit (DocVault)
            </button>
          ) : null}

          {isOfficer && p.state === 'ISSUED' ? (
            <div className="flex gap-2">
              <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/permits/${id}/complete`))}>
                Mark works completed
              </button>
              <div className="flex flex-1 gap-2">
                <input className="field-input" placeholder="Revoke reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
                <button className="btn-danger" disabled={busy || revokeReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m31/permits/${id}/revoke`, { reason: revokeReason }))}>
                  Revoke
                </button>
              </div>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Applied {formatDateTime(p.createdAt)} · last updated {formatDateTime(p.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function ApplyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState<EnvPermitType>('TREE_REMOVAL');
  const [parcelNo, setParcelNo] = useState('');
  const [description, setDescription] = useState('');
  const [treeCount, setTreeCount] = useState('1');
  const [species, setSpecies] = useState('');
  const [volumeCubicM, setVolumeCubicM] = useState('');
  const [dischargeType, setDischargeType] = useState('');
  const [volumeM3PerDay, setVolumeM3PerDay] = useState('');
  const [eiaCaseId, setEiaCaseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const details = () => {
    if (type === 'TREE_REMOVAL') return { treeCount: Number.parseInt(treeCount, 10) || 1, species: species || undefined };
    if (type === 'DISCHARGE') return { dischargeType: dischargeType || undefined, volumeM3PerDay: volumeM3PerDay ? Number.parseFloat(volumeM3PerDay) : undefined };
    return { volumeCubicM: volumeCubicM ? Number.parseFloat(volumeCubicM) : undefined };
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m31/permits', {
        type,
        parcelNo,
        description,
        details: details(),
        eiaCaseId: eiaCaseId || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New environmental permit application" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Permit type">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as EnvPermitType)}>
              {PERMIT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Parcel number">
            <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
          </Field>
        </div>
        <Field label="Description">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        {type === 'TREE_REMOVAL' ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tree count">
              <input className="field-input" type="number" min="1" value={treeCount} onChange={(e) => setTreeCount(e.target.value)} />
            </Field>
            <Field label="Species (optional)">
              <input className="field-input" value={species} onChange={(e) => setSpecies(e.target.value)} />
            </Field>
          </div>
        ) : type === 'DISCHARGE' ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Discharge type">
              <input className="field-input" value={dischargeType} onChange={(e) => setDischargeType(e.target.value)} />
            </Field>
            <Field label="Volume (m³/day)">
              <input className="field-input" type="number" value={volumeM3PerDay} onChange={(e) => setVolumeM3PerDay(e.target.value)} />
            </Field>
          </div>
        ) : (
          <Field label="Volume (m³)">
            <input className="field-input" type="number" value={volumeCubicM} onChange={(e) => setVolumeCubicM(e.target.value)} />
          </Field>
        )}

        <Field label="Linked EIA case id (optional — required if a higher-impact permit needs prior EIA clearance)">
          <input className="field-input" value={eiaCaseId} onChange={(e) => setEiaCaseId(e.target.value)} />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !parcelNo || description.trim().length < 3} onClick={submit}>
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** "Simple mobile flow for tree-removal permits: photo + GPS" — POST /permits/quick-tree (T1+). */
function QuickTreeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [parcelNo, setParcelNo] = useState('');
  const [description, setDescription] = useState('');
  const [treeCount, setTreeCount] = useState('1');
  const [species, setSpecies] = useState('');
  const [photo, setPhoto] = useState<FileHashValue | null>(null);
  const [gpsLat, setGpsLat] = useState('');
  const [gpsLng, setGpsLng] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const useDeviceLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not available in this browser — enter coordinates manually.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLat(pos.coords.latitude.toFixed(6));
        setGpsLng(pos.coords.longitude.toFixed(6));
        setLocating(false);
      },
      () => {
        setError('Could not read device location — enter coordinates manually.');
        setLocating(false);
      },
    );
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!photo) throw new Error('A photo is required');
      await api.post('/api/v1/m31/permits/quick-tree', {
        parcelNo,
        description,
        treeCount: Number.parseInt(treeCount, 10) || 1,
        species: species || undefined,
        photoDocRef: photo.docRef,
        photoSha256: photo.sha256,
        gpsLat: Number.parseFloat(gpsLat),
        gpsLng: Number.parseFloat(gpsLng),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Failed to submit');
    } finally {
      setBusy(false);
    }
  };

  const valid = parcelNo.trim().length > 0 && description.trim().length >= 3 && photo !== null && Number.isFinite(Number.parseFloat(gpsLat)) && Number.isFinite(Number.parseFloat(gpsLng));

  return (
    <Modal title="Quick tree removal permit — mobile" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">Fewer fields than a general application: snap a photo, capture GPS, done (POST /permits/quick-tree, T1+).</p>
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Parcel number">
          <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
        </Field>
        <Field label="Description">
          <input className="field-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Remove 1 dead coconut palm blocking access road" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tree count">
            <input className="field-input" type="number" min="1" value={treeCount} onChange={(e) => setTreeCount(e.target.value)} />
          </Field>
          <Field label="Species (optional)">
            <input className="field-input" value={species} onChange={(e) => setSpecies(e.target.value)} />
          </Field>
        </div>
        <FileHashField label="Photo of tree(s)" onChange={setPhoto} required />
        <div className="grid grid-cols-2 gap-3">
          <Field label="GPS latitude">
            <input className="field-input" type="number" step="0.000001" value={gpsLat} onChange={(e) => setGpsLat(e.target.value)} />
          </Field>
          <Field label="GPS longitude">
            <input className="field-input" type="number" step="0.000001" value={gpsLng} onChange={(e) => setGpsLng(e.target.value)} />
          </Field>
        </div>
        <button className="btn-secondary" type="button" disabled={locating} onClick={useDeviceLocation}>
          {locating ? 'Reading location…' : 'Use this device’s location'}
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !valid} onClick={submit}>
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
